// Defaults & Workout rebuild — the stage-appropriate action rail. The
// actions that matter for the case's CURRENT stage are promoted to
// buttons; everything else stays reachable through the "All actions"
// overflow (promotion is emphasis, not removal). Per-action gating
// mirrors the backend permissions (caseStages.canPerformAction).

import { Button, OverflowMenu } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import {
  ALL_CASE_ACTIONS,
  CASE_ACTION_LABELS,
  canPerformAction,
  promotedActionsFor,
  stageForStatus,
  type CaseAction,
} from "./caseStages";

interface StageActionRailProps {
  status: string;
  onAction: (action: CaseAction) => void;
}

export function StageActionRail({ status, onAction }: StageActionRailProps) {
  const auth = useAuth();
  const stage = stageForStatus(status);

  // closed-final is terminal: "No further events may be added." The old
  // ClosureCard hid its button once closed; the rail goes further and
  // offers NO writes on a closed case (it renders read-only via the
  // ?case= deep link from the history list).
  if (stage === "closed") return null;

  const permitted = (a: CaseAction) => canPerformAction(a, auth);
  const promoted = promotedActionsFor(stage).filter(permitted);
  const overflow = ALL_CASE_ACTIONS.filter(
    (a) => permitted(a) && !promoted.includes(a),
  );

  if (promoted.length === 0 && overflow.length === 0) return null;

  return (
    <div
      className="flex items-center gap-space-2 flex-wrap"
      data-testid="stage-action-rail"
      data-stage={stage}
    >
      {promoted.map((a, i) => (
        <Button
          key={a}
          variant={i === 0 ? "primary" : "secondary"}
          size="sm"
          onClick={() => onAction(a)}
          data-testid={`stage-action-${a}`}
        >
          {CASE_ACTION_LABELS[a]}
        </Button>
      ))}
      {overflow.length > 0 && (
        <OverflowMenu
          label="All actions"
          data-testid="stage-action-overflow"
          items={overflow.map((a) => ({
            label: CASE_ACTION_LABELS[a],
            onSelect: () => onAction(a),
          }))}
        />
      )}
    </div>
  );
}
